import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { getGetMeQueryKey, getGetStoreQueryKey, useUpdateStore, useUploadImage } from "@/api";
import type { Store } from "@/api/schemas/store";
import { useSavedFlash } from "@/hooks/settings/useSavedFlash";
import { getApiErrorMessage } from "@/lib/api/errors";
import { resolveMediaUrl } from "@/lib/media";
import { BUSINESS_TYPE_OPTIONS, CATEGORY_OPTIONS } from "@/lib/settings/constants";
import {
  generalSettingsSchema,
  type GeneralSettingsFormValues,
} from "@/lib/validation/settings.schemas";

const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

type Option = { value: string; label: string; translate: boolean };

/** Known options plus the store's current value when the list doesn't contain it (old data). */
function withCurrentValue(
  options: readonly { value: string; labelKey: string }[],
  current?: string,
): Option[] {
  const list: Option[] = options.map((option) => ({
    value: option.value,
    label: option.labelKey,
    translate: true,
  }));
  if (current && !options.some((option) => option.value === current)) {
    list.push({ value: current, label: current, translate: false });
  }
  return list;
}

/** General tab: store name, category, logo, warehouse address and (owner only) IBAN. */
export function useSettingsGeneral(
  storeId: number | null,
  store: Store | undefined,
  isOwner: boolean,
) {
  const queryClient = useQueryClient();
  const { saved, flash } = useSavedFlash();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  const form = useForm<GeneralSettingsFormValues>({
    resolver: zodResolver(generalSettingsSchema),
    defaultValues: {
      name: "",
      category: "",
      business_type: "",
      description: "",
      warehouse_address: "",
      iban: "",
    },
  });

  useEffect(() => {
    if (!store) return;
    form.reset({
      name: store.name ?? "",
      category: store.category ?? "",
      business_type: store.business_type ?? "",
      description: store.description ?? "",
      warehouse_address: store.warehouse_address ?? "",
      iban: store.iban ?? "",
    });
  }, [store, form]);

  const refreshStore = useCallback(() => {
    if (storeId === null) return;
    void queryClient.invalidateQueries({ queryKey: getGetStoreQueryKey(storeId) });
    // Store name and logo also show in the navbar's store switcher.
    void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
  }, [queryClient, storeId]);

  const { mutate: updateStore, isPending: isSubmitting } = useUpdateStore({
    mutation: {
      onSuccess: () => {
        refreshStore();
        flash();
      },
      onError: (error) =>
        setSaveError(getApiErrorMessage(error) ?? "ذخیره تغییرات انجام نشد؛ دوباره تلاش کنید"),
    },
  });

  const { mutate: uploadImage, isPending: isUploadingLogo } = useUploadImage({
    mutation: {
      onSuccess: (response) => {
        if (response.status !== 201 || storeId === null) return;
        updateStore({ id: storeId, data: { logo_url: response.data.url } });
      },
      onError: (error) => setLogoError(getApiErrorMessage(error) ?? "بارگذاری لوگو انجام نشد"),
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (storeId === null) return;
    setSaveError(null);
    const { iban, ...rest } = values;
    // Only the owner may change the payout IBAN; the backend rejects it for everyone else.
    updateStore({ id: storeId, data: isOwner ? { ...rest, iban: iban || undefined } : rest });
  });

  const onLogoSelect = useCallback(
    (file: File) => {
      setLogoError(null);
      if (!LOGO_TYPES.includes(file.type)) {
        setLogoError("فقط تصویر PNG، JPG یا WEBP قابل بارگذاری است");
        return;
      }
      if (file.size > LOGO_MAX_BYTES) {
        setLogoError("حجم لوگو باید کمتر از ۲ مگابایت باشد");
        return;
      }
      uploadImage({ data: { file } });
    },
    [uploadImage],
  );

  const categoryOptions = useMemo(
    () => withCurrentValue(CATEGORY_OPTIONS, store?.category),
    [store?.category],
  );
  const businessTypeOptions = useMemo(
    () => withCurrentValue(BUSINESS_TYPE_OPTIONS, store?.business_type),
    [store?.business_type],
  );

  return {
    form,
    onSubmit,
    logoUrl: resolveMediaUrl(store?.logo_url),
    isLoading: !store,
    isSubmitting,
    isUploadingLogo,
    saved,
    saveError,
    logoError,
    onLogoSelect,
    canEditIban: isOwner,
    categoryOptions,
    businessTypeOptions,
  };
}

export type SettingsGeneralState = ReturnType<typeof useSettingsGeneral>;
